/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCell, TableRow, Typography } from '@mui/material';

import { HUChip } from '../../../../../../../../elements/HUChip/HUChip';
import { Namespaces } from '../../../../../../../../i18n';
import { useTableStyles } from '../../../../../../../../styles/table';
import { DatasetImportOperation } from '../../../../../../../../types/dataset/DatasetImportOperation';
import { DatasetImportOperationStatus } from '../../../../../../../../types/dataset/DatasetImportOperationStatus';
import { getDatasetImportOperationStatus } from '../../../../../../../../utils/getDatasetImportOperationStatus';
import { getDSImportOperationStatusColor } from '../../../../../../../../utils/getDSImportOperationStatusColor';

type DatasetImportsTableVersionRowProps = {
  operation: DatasetImportOperation;
};

const getDatasetImportDate = (importFinish: string | null) => {
  return importFinish ? DateTime.fromISO(importFinish).toLocaleString(DateTime.DATETIME_FULL) : <>&mdash;</>;
};

export function DatasetImportsTableOperationRow(props: DatasetImportsTableVersionRowProps) {
  const { operation } = props;
  const tableClasses = useTableStyles();
  const { t } = useTranslation(Namespaces.Cluster);

  const operationFinishDate = useMemo<string | React.ReactElement<any>>(
    () => getDatasetImportDate(operation.importFinish),
    [operation.importFinish],
  );

  const operationStartDate = useMemo<string | React.ReactElement<any>>(
    () => getDatasetImportDate(operation.importStart),
    [operation.importStart],
  );

  const operationStatus = useMemo<DatasetImportOperationStatus>(
    () => getDatasetImportOperationStatus(operation),
    [operation],
  );

  const statusMessage = useMemo<string | string[]>(
    () => [operation.result ?? '', operationStatus].filter(v => v).map(v => `details.datasets.dataset.status.${v}`),
    [operation.result, operationStatus],
  );

  return (
    <TableRow>
      <TableCell className={tableClasses.bodyTableCell} />
      <TableCell className={tableClasses.bodyTableCell} />
      <TableCell className={tableClasses.bodyTableCell} />
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {operation.type === 'import'
            ? t('details.datasets.dataset.operation_type.import')
            : t('details.datasets.dataset.operation_type.editable')}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {operationStartDate}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {operationFinishDate}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <HUChip
          size="small"
          color={getDSImportOperationStatusColor(operationStatus)}
          variant="outlined"
          label={t(statusMessage)}
        />
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell} />
    </TableRow>
  );
}
